import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  useListCustomAgentConfigsQuery,
  useListNotificationRulesQuery
} from '@axilon-apps/domains-ontology-ui-state'

import type { ConfigureType } from './ConfigurePage'
import { hrefForLastVisited, readLastVisited, writeLastVisited } from './last-visited'

interface UseConfigureLastVisitedOptions {
  type: ConfigureType
  id: string | null
  /**
   * True only for the bare `/configure` landing. Typed list URLs are explicit
   * list destinations and must clear any remembered detail.
   */
  isBareHome: boolean
}

/**
 * Persist the current Configure destination. On an initial bare landing,
 * reopen a valid remembered detail after the lists load. Every explicit list
 * visit clears the remembered detail so returning later stays on the list.
 */
export function useConfigureLastVisited({
  type,
  id,
  isBareHome
}: UseConfigureLastVisitedOptions): void {
  const navigate = useNavigate()
  const phaseRef = useRef<'landing' | 'active'>(isBareHome ? 'landing' : 'active')
  const hasSelection = id !== null && id !== ''

  const {
    data: customConfigs = [],
    isLoading: customLoading,
    isUninitialized: customUninitialized,
    isError: customError
  } = useListCustomAgentConfigsQuery(undefined)
  const {
    data: rules = [],
    isLoading: rulesLoading,
    isUninitialized: rulesUninitialized,
    isError: rulesError
  } = useListNotificationRulesQuery(undefined)
  const ready =
    !customLoading &&
    !customUninitialized &&
    !customError &&
    !rulesLoading &&
    !rulesUninitialized &&
    !rulesError

  useEffect(() => {
    if (hasSelection) {
      phaseRef.current = 'active'
      writeLastVisited({ type, id })
      return
    }

    if (!isBareHome || phaseRef.current === 'active') {
      phaseRef.current = 'active'
      writeLastVisited(null)
      return
    }

    if (!ready) return
    const remembered = readLastVisited()
    const exists =
      remembered !== null &&
      (remembered.type === 'custom'
        ? customConfigs.some((config) => config.id === remembered.id)
        : rules.some((rule) => rule.id === remembered.id))
    phaseRef.current = 'active'

    if (remembered !== null && exists) {
      navigate(hrefForLastVisited(remembered), { replace: true })
    } else {
      writeLastVisited(null)
    }
  }, [type, id, isBareHome, hasSelection, ready, customConfigs, rules, navigate])
}
